import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, Host, ElementRef, HostListener, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { Entry } from 'contentful';

import { AnalyticsService } from '@src/app/shared/services/fb-analytics.service';
import { debounce } from '@src/app/shared/services/general.service';
import { ContentService } from '@src/app/shared/services/content.service';

interface FilterOptions {
  type: string;
  data: {
    name: string;
    count: number;
    selected: boolean
  }
}

@Component({
  selector: 'app-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss']
})

export class SearchFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entries: Entry<any>[];
  @Output() searchChange = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<object[]>();

  @HostListener (
    'window:resize', ['event']
  )
  handleResize(): void {
    this.filterPos();
  }
  
  searchTerm: BehaviorSubject<string> = new BehaviorSubject<string>('');
  filterCriteria: BehaviorSubject<object[]> = new BehaviorSubject<object[]>([]);
  filterSearch: string = '';
  
  filterData: FilterOptions[] = [];
  menuOptions: FilterOptions[] = [];

  isFilterOpen: boolean;
  isApplyBtnActive: boolean;
  isResetBtnActive: boolean;
  isResetAllBtnActive: boolean;

  menuBtnEl: EventTarget;
  menuPosLeft: number;

  gaSearchInit: boolean = false;
  gaFilterInit: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private analyticsService: AnalyticsService,
    private contentService: ContentService,
    private searchComp: ElementRef
  ) { }

  get filteredMenuOpts() {
    const s = (this.filterSearch || '').trim().toLowerCase();

    if (!s)
      return this.menuOptions || [];

    this.btnActive();
    return (this.menuOptions || []).filter(opt => (opt.data?.name || '').toLowerCase().includes(s));
  }

  @debounce(500)
  searchEntries($event: Event): void {
    const v = ($event.target as HTMLInputElement).value || '';
    this.searchTerm.next(v);
    this.searchChange.emit(v);
  }

  @debounce(2000)
  trackSearch($event: Event): void {
    this.analyticsService.trackInput($event)
  }

  FilterEntries(type: string, $event?: Event): void {
    if ($event)
      this.analyticsService.trackInput($event);

    if (!type || type === '') {
      this.filterCriteria.next([]);
    } else {
      const criteria = {
        type: type,
        values: this.menuOptions.filter(e => e.data.selected).map(e => e.data.name)
      };

      this.filterCriteria.next(
        this.filterCriteria.value.length ? 
        this.filterCriteria.value.filter(
          e => e['type'] !== type
        ).concat(
          criteria.values.length ? criteria : []
        ).sort((a, b) => 
          a['type'].localeCompare(b['type'])
        ) : 
        [criteria.values.length ? criteria : []]
      );
    }

    this.filterChange.emit(this.filterCriteria.value);
    this.btnActive();
  }

  btnActive($event?: Event): void {
    const filterType = this.menuOptions[0]?.type,
          activeFilters = this.filterCriteria.value.filter(e => e['type'] === filterType).map(e => e['values']).flat(),
          selFiltersAll = this.filterData.filter(e => e.data.selected),
          selFiltersMenu = this.menuOptions.filter(e => e.data.selected).map(e => e.data.name);

    if ($event)
      this.analyticsService.trackInput($event);

    if (selFiltersAll.length || activeFilters.length || this.filterSearch) {
      this.isResetAllBtnActive = true;
    } else {
      this.isResetAllBtnActive = false;
    }

    if (selFiltersMenu.length) {
      this.isResetBtnActive = true;
    } else {
      this.isResetBtnActive = false;
    }

    if (selFiltersMenu.length !== activeFilters.length ||
        !activeFilters.every((e, i) => e === selFiltersMenu[i])) {
      this.isApplyBtnActive = true;
    } else {
      this.isApplyBtnActive = false;
    }
  }

  getFilterCrit(type: string, arr: any[]): FilterOptions[] {
    const opts: FilterOptions[] = [];

    arr.flat().forEach((e) => {
      const critMatch = opts.find(e2 => e2.data.name === e);

      if (!critMatch) {
        opts.push({
          type: type,
          data: {
            name: e,
            count: 1,
            selected: false
          }
        })
      } else {
        critMatch.data.count += 1
      }
    });
    
    return opts;
  }

  isActiveCrit(type: string, value: string): boolean {
    const currentCrit = this.filterCriteria.value;

    if (
      currentCrit.length &&
      currentCrit.find(e => e['type'] === type)
    )
      return this.filterCriteria.value.find(e => e['type'] === type)['values'].includes(value);

    return false;
  }

  filterPos($event?: Event): void {
    if ($event)
      this.menuBtnEl = $event.currentTarget;
    
    if (this.menuBtnEl && window.innerWidth > 1280) {
      this.menuPosLeft = this.menuBtnEl['offsetLeft'] - 48 || 0;
    } else {
      this.menuPosLeft = this.searchComp.nativeElement['offsetLeft'] || 0;
    }
  }

  openFilter(crit: string = 'categories', $event?: Event): void {
    if ($event) {
      this.analyticsService.trackInput($event);
      this.filterPos($event);
    }

    this.menuOptions = this.filterData.filter(e => e.type === crit);
    this.btnActive();
    this.isFilterOpen = true;
    this.document.body.dataset.filterMenuOpen = 'true';
  }

  closeFilter($event?: Event): void {
    const evAsHTML = $event?.target as HTMLElement;

    if ($event) {
      if (!evAsHTML.dataset.content)
        evAsHTML.dataset.content = 'Blog Home Filter Close';

      this.analyticsService.trackInput($event);
    }

    this.menuOptions = [];
    this.isFilterOpen = false;
    this.document.body.dataset.filterMenuOpen = 'false';
  }

  resetAllFilters($event?: Event): void {
    if ($event)
      this.analyticsService.trackInput($event);

    this.filterData.map(e => {e.data.selected = false});
    this.FilterEntries('');
    // this.btnActive();
  }

  // Lifecycle Hooks

  ngOnInit(): void {
    this.searchTerm.pipe(
      distinctUntilChanged(),
      switchMap((str: string) =>
        this.contentService.searchEntries(
          this.entries,
          str,
          this.gaSearchInit
        )
      )
    )
    .subscribe(() => {
      this.gaSearchInit = true;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entries && this.entries.length) {
      this.filterData.push(
        ...this.getFilterCrit(
          'categories',
          this.entries.map((e): string[] => {
            return e.fields.categories
          })
        ),
        ...this.getFilterCrit(
          'tags',
          this.entries.map((e): string[] => {
            return e.metadata.tags.map(
              e2 => this.contentService.tagIDtoName(e2.sys.id)
            )
          })
        )
      )
    }
  }

  ngOnDestroy(): void {
    this.searchTerm.unsubscribe();
    !this.gaSearchInit;
  }
}
